interface ISiteContainer {

    Config: ISiteStartSectionItem;
    SiteConfig: SiteConfig;

    post(request: ISiteRequest): Promise<boolean>;
    request(card: ISiteCard, request: ISiteRequest): Promise<boolean>;
    on<TEvent extends Event>(val: string, func: (e: TEvent) => any): (e: TEvent) => any;
    off<TEvent extends Event>(val: string, func: ((e: TEvent) => any) | null): void;

    back(): Promise<void>;
    redraw(): void;
    appendCard(card: ISiteCard): Promise<void>;
    makeCardActive(card: ISiteCard): Promise<void>;
    replaceCard(oldCard: ISiteCard, newCard: ISiteCard): Promise<void>;
    removeNextCards(card: ISiteCard): Promise<void>;
    removeCard(card: ISiteCard): Promise<void>;

    remove(removeInner?: boolean): void;
}
interface ISiteRequest {
    request: IActionInfo;
    path?: string[]; 
    model?: string | Blob | FormData;
    options?: {
        override: boolean | undefined;
    }
}

class SiteContainer
    extends QueryElement<HTMLElement>
    implements ISiteContainer {

    private readonly _site: Site;
    private readonly _cards: IQueryElement;
    private readonly _requester: ISiteCard;
    private readonly _loader: SiteLoader;
    private readonly _config: ISiteStartSectionItem;
    private _positioner?: ISiteCardPositioner;
    private _currentEvent: Event | undefined;

    get Config() { return this._config; }
    get SiteConfig() { return this._site.Config; }

    constructor(site: Site, config: ISiteStartSectionItem) {
        super(document.createElement("DIV"));

        this._site = site;
        this._config = config;
        this._requester = new SiteCardContainerWrapper(this);

        let title = QueryElement.QueryElement("DIV")
            .addClass("title")
            .append(QueryElement.QueryElement("i").setHtml(config.icon || ""))
            .append(QueryElement.QueryElement("span").setHtml(config.title || "-"))
            .appendTo(this);

        this._cards = QueryElement.QueryElement("DIV")
            .addClass("cards")
            .appendTo(this);

        this._loader = new SiteLoader()
            .appendTo(this);

        this.on("mousedown", (e) => {
            this._currentEvent = e;
        })
        this.on("mouseup", (e) => {
            if (this._currentEvent === undefined)
                return;
            
            let target = this._currentEvent.target as HTMLElement;

            // Check if it is a label
            let isLabel = false;
            let element = QueryElement.get<QueryElement<HTMLElement>>(target);
            if (element instanceof SiteLabel) {
                isLabel = true;
            } else {
                element.parentAll<ISiteLabel>(a => {
                    if (a instanceof SiteLabel) {
                        isLabel = true;
                    }
                });
            }

            if (!isLabel) {
                let card = this.getActiveCard();
                let label = card && card.getActiveFocusable();
                if (label && label instanceof SiteLabel)
                    label.makeInactive();
            }
        });
        this.on("qts.functionKey", (e: CustomEvent) => {
            let activeCard = this.getActiveCard();

            if(activeCard)
                activeCard.trigger("qts.functionKey", false, e.detail);
        });
        this.on("qts.observer.event", (e: CustomEvent) => {
            let lastCard = this._cards.lastChild<SiteCard>();

            if(lastCard)
                lastCard.trigger("qts.observer.event", false, e.detail);
        });
        this.on("qts.observer.change", (e: CustomEvent) => {
            this.getCards()
                .all<SiteCard, any>(a => a.trigger("qts.observer.change", false, e.detail));
        });
        this.addClass("container");
    }


    async start(request: IActionTypeRequest) {
        // Create a one time requester
        let requester = new SiteCardContainerWrapper(this);

        // Do the request
        await requester.request(request);
    }

    redraw() {
        this._positioner?.redraw();
    }

    resize() {
        let position = this.position();
        if(position === null)
            return;

        let positioner = position.width <= Site.mobileWidth ? SiteCardPositionerMobile : SiteCardPositionerDesktop;

        // If we have another positioner, destruct and create new one
        if(!(this._positioner instanceof positioner)) {
            if(this._positioner !== undefined)
                this._positioner.destruct();
            
            // Construct new one:
            this._positioner = new positioner(
                this,
                this._cards.query(".card").all<SiteCard, SiteCard>(a => a)
            );
        }

        this._positioner.resize(position);
    }

    async post(request: ISiteRequest): Promise<boolean> {
        return await this._site.post(request);
    }
    async request(card: ISiteCard, request: ISiteRequest): Promise<boolean> {
        this._loader.show();
        this.trigger("qts.loading", false);
        try {
            let command = new SiteContainerRequestCommand(this._site, this, card, request);
            return await command.execute();
        } catch (e) {
            // Fetch exceptions:
            if (e instanceof UnauthorizedException)
            {
                var action = new SiteActionUrl(card, { type: "Url", url: "/", newWindow: false}, null);
                await action.execute();
                return false;
            }
            
            // Unknown exception:
            if (navigator.onLine) {
                this._site.addMessage(new SiteMessage({
                    title: "Something went wrong :(",
                    message: "We couldn't complete the request, please contact the helpdesk, press F1 to find out how.",
                    sort: "Error"
                }));
            } else {
                this._site.addMessage(new SiteMessage({
                    title: "No internet connection",
                    message: "There is no internet available please check your network and try again.",
                    sort: "Error"
                }));
            }
            console.error(e);
            return false;
        } finally {
            this.trigger("qts.loaded", false);
            this._loader.hide();
        }
    }

    async makeActive() {
        if(this.isRemoved() || this.containsClass("active"))
            return false;
        
        this.addClass("active");
        this.trigger("qts.active", false);

        let card = this._cards.lastChild<SiteCard>();
        card && await this.makeCardActive(card);

        return true;
    }
    async makeInactive() {
        if(this.isRemoved() || !this.containsClass("active"))
            return false;

        // Make active card inactive:
        let card = this.getActiveCard();
        card && card.makeInactive();
        
        // And now the container
        this.removeClass("active");
        this.trigger("qts.inactive", false);

        return true;
    }
    public isActive() {
        return this.containsClass("active");
    }
    public hasFocus() {
        let hasFocus = this.query(":focus").first();
        return hasFocus !== null;
    }

    static async getCardView(view: string) {
        let cardRequest = await new QueryRequest(view)
            .request();

        return await cardRequest.text();
    }
    getCards() {
        return this._cards.query(".card");
    }
    getActiveCard() {
        return this._cards.query(".card.active")
            .first<SiteCard>();
    }
    async back() {
        let activeCard = this._cards.lastChild<SiteCard>();
        
        if (activeCard) {
            await activeCard.back();
        }
    }
    async appendCard(card: SiteCard) {
        this._cards.append(card);
        this._positioner && this._positioner.addCard(card);
        
        // Add history
        this._site.getHistory().add();
        
        // Only start when we are active
        if (this.isActive())
            await card.start();
    }
    async replaceCard(oldCard: ISiteCard, newCard: ISiteCard) {
        // Check if oldCard is removed
        if (oldCard.isRemoved()) {
            return await newCard.remove();
        }
        
        // If not, replace card content
        if(oldCard instanceof QueryElement && newCard instanceof QueryElement)
            this._cards.replace(oldCard, newCard);
        
        this._positioner && this._positioner.replaceCard(oldCard, newCard);

        // Make card active if it was active:
        if(this.isActive() && oldCard.isActive())
            await this.makeCardActive(newCard);
        
        // Do not await removing the old card
        oldCard.remove();
    }
    async makeCardActive(card: ISiteCard) {
        await this._site.makeContainerActive(this);

        let activeCard = this.getActiveCard();

        if (card === activeCard)
            return;

        if (activeCard !== null)
            activeCard.makeInactive();
        
        this.trigger("qts.card", false, card);
        card.makeActive();
    }
    async removeCard(card: ISiteCard) {
        if (card.isRemoved())
            return;
        
        let toActivate = card.next<SiteCard>() || card.prev<SiteCard>();

        // Do we need to stay inside this container?
        if (this._site.Config.settings.includes("NoNavigation")) {
            // Yes, only remove the card when we have another card left
            if (toActivate !== null) {
                this._positioner?.removeCard(card);
                await card.remove();
                await this.makeCardActive(toActivate);
            }
        } else {
            // No, just remove the card
            this._positioner?.removeCard(card);
            await card.remove();

            if (toActivate !== null)
                await this.makeCardActive(toActivate);
            else if (card instanceof SiteCard)
                await this._site.removeContainer(this);
        }
    }
    async removeNextCards(card: ISiteCard) {
        let cards: SiteCard[] = [];
        card.nextAll<SiteCard>(a => { cards.push(a); });
        
        for (let toRemove of cards.reverse()) {
            this.removeCard(toRemove);
        };
    }

    async remove(removeInner = true) {
        await super.remove(removeInner);
    }

}

class SiteCardContainerWrapper
    implements ISiteCard {

    private _container: ISiteContainer;

    get Model(): SiteModel { return new SiteModel({}); }
    get ModelData(): SiteModel { return new SiteModel({}); }
    get Container(): ISiteContainer { return this._container; }

    constructor(container: ISiteContainer) {
        this._container = container;
    }
    trigger(val: string, bubble: boolean): this {
        return this;
    }
    async back(): Promise<void> {
        await this._container.removeCard(this);
    }
    async refresh() {
        return true;
    }
    async remove() {
        await this._container.remove();
    }

    addValidation(path: string[], validation: ISiteValidation) {
        return;
    }
    clearValidation() {
        return;
    }

    keyPress(keyCodes: number[]): void {
        return;
    }
    async post(request: IActionTypeRequest): Promise<boolean> {
        return true;
    }
    request(request: IActionTypeRequest): Promise<boolean> {
        return this._container.request(this, { request: request.actionLink.actionInfo });
    }
    getRequest() {
        return undefined;
    }
    makeFocusableActive(focusable: ISiteFocusable) {
        return this;
    }
    makePrevFocusableActive(focusable: ISiteFocusable) {
        return this;
    }
    makeNextFocusableActive(focusable: ISiteFocusable) {
        return this;
    }
    
    getAttr(attr: string): string | null {
        return null;
    }
    setAttr(attr: string, content: string): this {
        return this;
    }
    
    isLarge(): boolean {
        return false;
    }
    isActive(): boolean {
        return true;
    }
    isRemoved(): boolean {
        return false;
    }
    makeActive(): boolean {
        return true;
    }
    makeInactive(): void {
        return;
    }
    next<T extends IQueryElement>(): T | null {
        return null;
    }
    nextAll<T extends IQueryElement>(func: (item: T) => boolean | void) {
        return this;
    }
    prev<T extends IQueryElement>(): T | null {
        return null;
    }
    containsClass(string: string) {
        return false;
    }
    position() {
        return null;
    }
    style() {
        return undefined;
    }
}